import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  Button,
  Spinner,
  Image,
  Badge,
  Table,
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import PageLayout from "../../../layouts/PageLayout";

export default function ViewMedicalCare() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [medical, setMedical] = useState(null);
  const [loading, setLoading] = useState(true);

  const AuthToken = localStorage.getItem("AuthUser");

  /*
  |--------------------------------------------------------------------------
  | Axios Authorization
  |--------------------------------------------------------------------------
  */

  axios.defaults.headers.common["Authorization"] = `Bearer ${AuthToken}`;

  /*
  |--------------------------------------------------------------------------
  | Fetch Medical Care
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    fetchMedicalCare();
  }, [id]);

  const fetchMedicalCare = async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/medical-care/${id}`
      );

      const data = response?.data?.data || response?.data || null;

      setMedical(data);
    } catch (error) {
      console.error("Medical Care Error:", error);

      Swal.fire({
        title: "Error!",
        text:
          error?.response?.data?.message ||
          "Failed to load medical care record.",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Helpers
  |--------------------------------------------------------------------------
  */

  const imageUrl = (path) => {
    if (!path) return "";

    if (
      typeof path === "string" &&
      (path.startsWith("http://") || path.startsWith("https://"))
    ) {
      return path;
    }

    return `${process.env.REACT_APP_IMG_URL}/${String(path).replace(
      /^\/+/,
      ""
    )}`;
  };

  const formatAmount = (amount) => {
    if (
      amount === null ||
      amount === undefined ||
      amount === ""
    ) {
      return "PKR 0";
    }

    const number = Number(amount);

    if (Number.isNaN(number)) {
      return "PKR 0";
    }

    return `PKR ${number.toLocaleString("en-PK", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatDate = (date) => {
    if (!date) return "N/A";

    const parsed = new Date(date);

    if (Number.isNaN(parsed.getTime())) {
      return date;
    }

    return parsed.toLocaleDateString("en-PK", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatDateTime = (date) => {
    if (!date) return "N/A";

    const parsed = new Date(date);

    if (Number.isNaN(parsed.getTime())) {
      return date;
    }

    return parsed.toLocaleString("en-PK", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getTreatmentWay = (way) => {
    if (String(way) === "1") {
      return "Mobile Health Van";
    }

    if (String(way) === "2") {
      return "Referred to Hospital";
    }

    return "N/A";
  };

  const getTreatmentBadge = (way) => {
    if (String(way) === "1") {
      return "success";
    }

    if (String(way) === "2") {
      return "primary";
    }

    return "secondary";
  };

  /*
  |--------------------------------------------------------------------------
  | Medicines
  |--------------------------------------------------------------------------
  */

  const getMedicines = () => {
    if (!medical) return [];

    let medicines = medical.medicines;

    if (!medicines) {
      return [];
    }

    if (typeof medicines === "string") {
      try {
        medicines = JSON.parse(medicines);
      } catch (error) {
        console.error("Medicine JSON Parse Error:", error);
        return [];
      }
    }

    if (!Array.isArray(medicines)) {
      return [];
    }

    return medicines;
  };

  const medicines = getMedicines();

  /*
  |--------------------------------------------------------------------------
  | Expenses
  |--------------------------------------------------------------------------
  */

  const medicineAmount = Number(
    medical?.medicine_amount || 0
  );

  const doctorFee = Number(
    medical?.doctor_fee || 0
  );

  const calculatedTotal =
    medicineAmount + doctorFee;

  const totalAmount =
    medical?.total_amount !== null &&
    medical?.total_amount !== undefined &&
    medical?.total_amount !== ""
      ? Number(medical.total_amount)
      : calculatedTotal;

  /*
  |--------------------------------------------------------------------------
  | Loading
  |--------------------------------------------------------------------------
  */

  if (loading) {
    return (
      <PageLayout>
        <div
          className="d-flex justify-content-center align-items-center"
          style={{
            minHeight: "500px",
          }}
        >
          <div className="text-center">
            <Spinner
              animation="border"
              variant="primary"
              style={{
                width: "3rem",
                height: "3rem",
              }}
            />

            <div className="mt-3 text-muted">
              Loading medical care details...
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | No Data
  |--------------------------------------------------------------------------
  */

  if (!medical) {
    return (
      <PageLayout>
        <Card className="shadow-sm">
          <Card.Body className="text-center p-5">
            <div
              className="mb-3"
              style={{
                fontSize: "50px",
                opacity: 0.4,
              }}
            >
              🏥
            </div>

            <h5>
              Medical care record not found
            </h5>

            <p className="text-muted">
              The requested medical care record could
              not be found.
            </p>

            <Button
              variant="secondary"
              onClick={() => navigate(-1)}
            >
              Go Back
            </Button>
          </Card.Body>
        </Card>
      </PageLayout>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | Render
  |--------------------------------------------------------------------------
  */

  return (
    <PageLayout>

      {/* ============================================================
          BREADCRUMB
      ============================================================ */}

      <Row>
        <Col xl={12}>
          <div className="mc-card mb-3">
            <div className="mc-breadcrumb">

              <h3 className="mc-breadcrumb-title">
                Medical Care Details
              </h3>

              <ul className="mc-breadcrumb-list">
                <li className="mc-breadcrumb-item">
                  Medical Care
                </li>

                <li className="mc-breadcrumb-item">
                  View Details
                </li>
              </ul>

            </div>
          </div>
        </Col>
      </Row>

      {/* ============================================================
          MAIN HEADER
      ============================================================ */}

      <Card className="mb-4 shadow-sm">

        <Card.Header
          className="d-flex justify-content-between align-items-center"
          style={{
            padding: "18px 20px",
          }}
        >

          <div>
            <h5 className="mb-1">
              Medical Care Record
            </h5>

            <small className="text-muted">
              Record #{medical.id || id}
            </small>
          </div>

          <div className="d-flex gap-2">

            <Button
              variant="secondary"
              onClick={() => navigate(-1)}
            >
              ← Back
            </Button>

            {/* <Button
              variant="primary"
              onClick={() =>
                navigate(`/medical-care/edit/${medical.id || id}`)
              }
            >
              Edit
            </Button> */}

          </div>

        </Card.Header>

        <Card.Body>

          <Row>

            {/* Family */}

            <Col md={4} className="mb-4">
              <div className="text-muted small mb-1">
                Family Number
              </div>

              <h6 className="mb-0">
                {medical.person?.f_number ||
                  medical.f_number ||
                  "N/A"}
              </h6>
            </Col>

            {/* Beneficiary */}

            <Col md={4} className="mb-4">
              <div className="text-muted small mb-1">
                Family Beneficiary
              </div>

              <h6 className="mb-0">
                {medical.person?.beneficiary_name ||
                  medical.beneficiary_name ||
                  "N/A"}
              </h6>
            </Col>

            {/* Patient */}

            <Col md={4} className="mb-4">
              <div className="text-muted small mb-1">
                Patient Name
              </div>

              <h6 className="mb-0">
                {medical.patient_name || "N/A"}
              </h6>
            </Col>

            {/* Relation */}

            <Col md={4} className="mb-4">
              <div className="text-muted small mb-1">
                Relation
              </div>

              <h6 className="mb-0">
                {medical.relation || "N/A"}
              </h6>
            </Col>

            {/* Date */}

            <Col md={4} className="mb-4">
              <div className="text-muted small mb-1">
                Treatment Date
              </div>

              <h6 className="mb-0">
                {formatDate(
                  medical.date ||
                    medical.treatment_date ||
                    medical.created_at
                )}
              </h6>
            </Col>

            {/* Treatment Way */}

            <Col md={4} className="mb-4">

              <div className="text-muted small mb-2">
                Treatment Method
              </div>

              <Badge
                bg={getTreatmentBadge(
                  medical.treatment_way
                )}
                style={{
                  fontSize: "13px",
                  padding: "8px 12px",
                }}
              >
                {getTreatmentWay(
                  medical.treatment_way
                )}
              </Badge>

            </Col>

          </Row>

        </Card.Body>
      </Card>

      {/* ============================================================
          HOSPITAL DETAILS
      ============================================================ */}

      {String(medical.treatment_way) === "2" && (
        <Card className="mb-4 shadow-sm">

          <Card.Header>
            <h5 className="mb-0">
              Hospital & Doctor Information
            </h5>
          </Card.Header>

          <Card.Body>

            <Row>

              {/* Hospital */}

              <Col md={4} className="mb-4">
                <div className="text-muted small mb-1">
                  Hospital
                </div>

                <h6>
                  {medical.hospital?.name ||
                    medical.hospital_name ||
                    "N/A"}
                </h6>
              </Col>

              {/* City */}

              <Col md={4} className="mb-4">
                <div className="text-muted small mb-1">
                  City
                </div>

                <h6>
                  {medical.hospital?.city?.name ||
                    medical.hospital?.city_name ||
                    "N/A"}
                </h6>
              </Col>

              {/* Doctor */}

              <Col md={4} className="mb-4">
                <div className="text-muted small mb-1">
                  Doctor Name
                </div>

                <h6>
                  {medical.doctor_name || "N/A"}
                </h6>
              </Col>

              {/* Address */}

              <Col md={6} className="mb-4">
                <div className="text-muted small mb-1">
                  Hospital Address
                </div>

                <h6>
                  {medical.hospital?.address ||
                    "N/A"}
                </h6>
              </Col>

              {/* Contact */}

              <Col md={6} className="mb-4">
                <div className="text-muted small mb-1">
                  Hospital Contact
                </div>

                <h6>
                  {medical.hospital?.contact_number ||
                    "N/A"}
                </h6>
              </Col>

            </Row>

          </Card.Body>
        </Card>
      )}

      {/* ============================================================
          MOBILE HEALTH VAN MEDICINES
      ============================================================ */}

      {String(medical.treatment_way) === "1" && (
        <Card className="mb-4 shadow-sm">

          <Card.Header
            className="d-flex justify-content-between align-items-center"
          >

            <div>
              <h5 className="mb-0">
                Medicines Provided
              </h5>

              <small className="text-muted">
                Medicines provided to the patient
              </small>
            </div>

            <Badge bg="success">
              {medicines.length}{" "}
              {medicines.length === 1
                ? "Medicine"
                : "Medicines"}
            </Badge>

          </Card.Header>

          <Card.Body>

            {medicines.length > 0 ? (
              <div className="table-responsive">

                <Table
                  bordered
                  hover
                  responsive
                  className="mb-0"
                  style={{
                    verticalAlign: "middle",
                  }}
                >

                  <thead>
                    <tr>
                      <th style={{ width: "80px" }}>
                        #
                      </th>

                      <th>
                        Medicine
                      </th>

                      <th
                        style={{
                          width: "200px",
                        }}
                      >
                        Quantity
                      </th>
                    </tr>
                  </thead>

                  <tbody>

                    {medicines.map(
                      (medicine, index) => (
                        <tr
                          key={
                            medicine.medicine_id ||
                            index
                          }
                        >

                          <td>
                            {index + 1}
                          </td>

                          <td>
                            <strong>
                              {medicine.medicine_name ||
                                medicine.name ||
                                "N/A"}
                            </strong>
                          </td>

                          <td>
                            <Badge bg="secondary">
                              {medicine.quantity ||
                                "0"}
                            </Badge>
                          </td>

                        </tr>
                      )
                    )}

                  </tbody>

                </Table>

              </div>
            ) : (
              <div className="text-center text-muted p-4">
                No medicines recorded for this treatment.
              </div>
            )}

          </Card.Body>
        </Card>
      )}

      {/* ============================================================
          MEDICAL REASON
      ============================================================ */}

      <Card className="mb-4 shadow-sm">

        <Card.Header>
          <h5 className="mb-0">
            Medical Reason
          </h5>
        </Card.Header>

        <Card.Body>

          <div
            style={{
              whiteSpace: "pre-wrap",
              lineHeight: "1.8",
              minHeight: "50px",
            }}
          >
            {medical.medical_reason ||
              "No medical reason provided."}
          </div>

        </Card.Body>
      </Card>

      {/* ============================================================
          EXPENSE SUMMARY
      ============================================================ */}

      <Card className="mb-4 shadow-sm">

        <Card.Header>
          <h5 className="mb-0">
            Medical Expenses
          </h5>
        </Card.Header>

        <Card.Body>

          <Row>

            {/* Medicine */}

            <Col md={4} className="mb-3">

              <div
                className="p-3"
                style={{
                  border: "1px solid #e5e5e5",
                  borderRadius: "8px",
                }}
              >

                <div className="text-muted small">
                  Medicine Amount
                </div>

                <h5 className="mt-2 mb-0">
                  {formatAmount(
                    medicineAmount
                  )}
                </h5>

              </div>

            </Col>

            {/* Doctor */}

            <Col md={4} className="mb-3">

              <div
                className="p-3"
                style={{
                  border: "1px solid #e5e5e5",
                  borderRadius: "8px",
                }}
              >

                <div className="text-muted small">
                  Hospital Bill
                </div>

                <h5 className="mt-2 mb-0">
                  {formatAmount(
                    doctorFee
                  )}
                </h5>

              </div>

            </Col>

            {/* Total */}

            <Col md={4} className="mb-3">

              <div
                className="p-3"
                style={{
                  border: "1px solid #198754",
                  borderRadius: "8px",
                }}
              >

                <div className="text-muted small">
                  Total Medical Expense
                </div>

                <h5 className="mt-2 mb-0 text-success">
                  {formatAmount(
                    totalAmount
                  )}
                </h5>

              </div>

            </Col>

          </Row>

        </Card.Body>
      </Card>

      {/* ============================================================
          RECEIPTS
      ============================================================ */}

      {(medical.medicine_receipt ||
        medical.doctor_fee_receipt) && (
        <Card className="mb-4 shadow-sm">

          <Card.Header>
            <h5 className="mb-0">
              Payment Receipts
            </h5>
          </Card.Header>

          <Card.Body>

            <Row>

              {/* Medicine Receipt */}

              <Col md={6} className="mb-4">

                <h6 className="mb-3">
                  Medicine Bill Receipt
                </h6>

                {medical.medicine_receipt ? (
                  <div>

                    <div
                      style={{
                        border:
                          "1px solid #ddd",
                        borderRadius:
                          "8px",
                        padding: "8px",
                        background:
                          "#fafafa",
                      }}
                    >

                      <Image
                        src={imageUrl(
                          medical.medicine_receipt
                        )}
                        fluid
                        rounded
                        style={{
                          maxHeight:
                            "350px",
                          width: "100%",
                          objectFit:
                            "contain",
                        }}
                        onError={(e) => {
                          e.currentTarget.style.display =
                            "none";
                        }}
                      />

                    </div>

                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="mt-3"
                      onClick={() =>
                        window.open(
                          imageUrl(
                            medical.medicine_receipt
                          ),
                          "_blank"
                        )
                      }
                    >
                      View Receipt
                    </Button>

                  </div>
                ) : (
                  <div className="text-muted">
                    No medicine receipt uploaded.
                  </div>
                )}

              </Col>

              {/* Hospital Receipt */}

              <Col md={6} className="mb-4">

                <h6 className="mb-3">
                  Hospital Bill Receipt
                </h6>

                {medical.doctor_fee_receipt ? (
                  <div>

                    <div
                      style={{
                        border:
                          "1px solid #ddd",
                        borderRadius:
                          "8px",
                        padding: "8px",
                        background:
                          "#fafafa",
                      }}
                    >

                      <Image
                        src={imageUrl(
                          medical.doctor_fee_receipt
                        )}
                        fluid
                        rounded
                        style={{
                          maxHeight:
                            "350px",
                          width: "100%",
                          objectFit:
                            "contain",
                        }}
                        onError={(e) => {
                          e.currentTarget.style.display =
                            "none";
                        }}
                      />

                    </div>

                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="mt-3"
                      onClick={() =>
                        window.open(
                          imageUrl(
                            medical.doctor_fee_receipt
                          ),
                          "_blank"
                        )
                      }
                    >
                      View Receipt
                    </Button>

                  </div>
                ) : (
                  <div className="text-muted">
                    No hospital bill receipt uploaded.
                  </div>
                )}

              </Col>

            </Row>

          </Card.Body>
        </Card>
      )}

      {/* ============================================================
          VIDEO
      ============================================================ */}

      <Card className="mb-4 shadow-sm">

        <Card.Header>
          <h5 className="mb-0">
           Patient Video
          </h5>
        </Card.Header>

        <Card.Body>

          {medical.video_link ? (
            <div>

              <video
                width="100%"
                controls
                preload="metadata"
                style={{
                  maxHeight: "600px",
                  background: "#000",
                  borderRadius: "10px",
                  display: "block",
                }}
              >

                <source
                  src={imageUrl(
                    medical.video_link
                  )}
                  type="video/mp4"
                />

                Your browser does not support
                the video tag.

              </video>

            </div>
          ) : (
            <div
              className="text-center text-muted"
              style={{
                padding: "60px 20px",
              }}
            >
              <div
                style={{
                  fontSize: "45px",
                  opacity: 0.4,
                }}
              >
                🎥
              </div>

              <div className="mt-2">
                No medical care video uploaded.
              </div>
            </div>
          )}

        </Card.Body>
      </Card>

      {/* ============================================================
          GALLERY
      ============================================================ */}

      <Card className="mb-4 shadow-sm">

        <Card.Header
          className="d-flex justify-content-between align-items-center"
        >

          <h5 className="mb-0">
            Medical Care Gallery
          </h5>

          <Badge bg="secondary">
            {medical.images?.length || 0} Images
          </Badge>

        </Card.Header>

        <Card.Body>

          {medical.images &&
          medical.images.length > 0 ? (

            <Row>

              {medical.images.map(
                (image, index) => (

                  <Col
                    lg={3}
                    md={4}
                    sm={6}
                    xs={12}
                    key={
                      image.id ||
                      index
                    }
                    className="mb-4"
                  >

                    <div
                      style={{
                        border:
                          "1px solid #ddd",
                        borderRadius:
                          "10px",
                        overflow:
                          "hidden",
                        background:
                          "#fff",
                      }}
                    >

                      <a
                        href={imageUrl(
                          image.img ||
                            image.image ||
                            image.path
                        )}
                        target="_blank"
                        rel="noreferrer"
                      >

                        <Image
                          src={imageUrl(
                            image.img ||
                              image.image ||
                              image.path
                          )}
                          fluid
                          style={{
                            width: "100%",
                            height: "220px",
                            objectFit:
                              "cover",
                            display:
                              "block",
                          }}
                        />

                      </a>

                      <div className="p-2 text-center">

                        <small className="text-muted">
                          Image {index + 1}
                        </small>

                      </div>

                    </div>

                  </Col>

                )
              )}

            </Row>

          ) : (

            <div
              className="text-center text-muted"
              style={{
                padding: "60px 20px",
              }}
            >

              <div
                style={{
                  fontSize: "45px",
                  opacity: 0.4,
                }}
              >
                🖼️
              </div>

              <div className="mt-2">
                No gallery images uploaded.
              </div>

            </div>

          )}

        </Card.Body>
      </Card>

      {/* ============================================================
          RECORD INFORMATION
      ============================================================ */}

      <Card className="mb-4 shadow-sm">

        <Card.Header>
          <h5 className="mb-0">
            Record Information
          </h5>
        </Card.Header>

        <Card.Body>

          <Row>

            <Col md={4} className="mb-3">

              <div className="text-muted small">
                Record ID
              </div>

              <strong>
                #{medical.id || id}
              </strong>

            </Col>

            <Col md={4} className="mb-3">

              <div className="text-muted small">
                Created At
              </div>

              <strong>
                {formatDateTime(
                  medical.created_at
                )}
              </strong>

            </Col>

            <Col md={4} className="mb-3">

              <div className="text-muted small">
                Last Updated
              </div>

              <strong>
                {formatDateTime(
                  medical.updated_at
                )}
              </strong>

            </Col>


          </Row>

        </Card.Body>
      </Card>

      {/* ============================================================
          BOTTOM ACTIONS
      ============================================================ */}

      <div
        className="d-flex justify-content-end gap-2 mb-4"
      >

        <Button
          variant="secondary"
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
{/* 
        <Button
          variant="primary"
          onClick={() =>
            navigate(
              `/medical-care/edit/${medical.id || id}`
            )
          }
        >
          Edit Medical Care
        </Button> */}

      </div>

    </PageLayout>
  );
}

